import axios from '../../utils/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dedupeEntities } from '../../utils/collectionUtils';

export const getAllUsers = createAsyncThunk(
  'admin/getAllUsers',
  async ({ page = 1, limit = 20, role, search, active, qualificationStatus }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (role) params.role = role;
      if (active !== undefined) params.active = active;
      if (search) params.search = search;
      if (qualificationStatus && qualificationStatus !== 'all') params.qualificationStatus = qualificationStatus;

      const res = await axios.get(`/users/admin/users`, { params });
      return res.data.data || res.data; // { users, total, page, limit }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  'admin/toggleUserStatus',
  async ({ userId, active }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/users/admin/users/${userId}/status`, { active });
      return res.data.data?.user || res.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update user status');
    }
  }
);

export const verifyBusiness = createAsyncThunk(
  'admin/verifyBusiness',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axios.patch(
        `/users/admin/business/${userId}/verification`,
        { action: 'approve' },
      );
      return res.data.data?.user || res.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to verify business');
    }
  }
);

export const unverifyBusiness = createAsyncThunk(
  'admin/unverifyBusiness',
  async ({ userId, rejectionReason }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(
        `/users/admin/business/${userId}/verification`,
        { action: 'reject', rejectionReason },
      );
      return res.data.data?.user || res.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to unverify business');
    }
  }
);

// ────────────────────────────────────────────────
// Slice
// ────────────────────────────────────────────────

const initialState = {
  users: [],
  total: 0,
  currentPage: 1,
  loading: false,
  error: null,
  lastActionUserId: null, // useful for optimistic updates or showing feedback
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // getAllUsers
    builder
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = dedupeEntities(action.payload.users);
        state.total = action.payload.pagination?.total ?? action.payload.total ?? 0;
        state.currentPage = action.payload.pagination?.page ?? action.payload.page ?? 1;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // toggleUserStatus
    builder
      .addCase(toggleUserStatus.pending, (state, action) => {
        state.lastActionUserId = action.meta.arg.userId;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const updatedUser = action.payload;
        const index = state.users.findIndex(u => u._id === updatedUser._id);
        if (index !== -1) {
          state.users[index] = { ...state.users[index], ...updatedUser };
        }
        state.lastActionUserId = null;
      })
      .addCase(toggleUserStatus.rejected, (state) => {
        state.lastActionUserId = null;
      });

    // verifyBusiness + unverifyBusiness (similar pattern)
    builder
      .addCase(verifyBusiness.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.users.findIndex(u => u._id === updated._id);
        if (index !== -1) state.users[index] = { ...state.users[index], ...updated };
      })
      .addCase(unverifyBusiness.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.users.findIndex(u => u._id === updated._id);
        if (index !== -1) state.users[index] = { ...state.users[index], ...updated };
      });
  },
});

export const { clearAdminError } = adminSlice.actions;

export const selectAdminUsers = (state) => state.admin.users;
export const selectAdminTotal = (state) => state.admin.total;
export const selectAdminLoading = (state) => state.admin.loading;
export const selectAdminError = (state) => state.admin.error;

export default adminSlice.reducer;
